import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Container,
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Alert,
  Button,
  CircularProgress,
  Divider,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PrintIcon from "@mui/icons-material/Print";
import dailyIntakeService from "../../services/dailyIntakeService";
import { useResolvedPatientId } from "../../hooks/useResolvedPatientId";

// Averaging helper for numeric fields across a week of entries.

const average = (values) => {
  const nums = values.filter((v) => v !== null && v !== undefined && !isNaN(v));
  if (nums.length === 0) return null;
  return nums.reduce((a, b) => a + Number(b), 0) / nums.length;
};

const formatBP = (entries) => {
  const sys = average(entries.map((e) => e.bloodPressureSystolic));
  const dia = average(entries.map((e) => e.bloodPressureDiastolic));
  if (sys === null || dia === null) return "—";
  return `${Math.round(sys)}/${Math.round(dia)}`;
};

// Groups entries into ISO-week buckets, keyed by the Monday of that week.
const groupByWeek = (entries) => {
  const weeks = {};
  entries.forEach((entry) => {
    const date = new Date(entry.date);
    const day = date.getDay();
    const diffToMonday = day === 0 ? -6 : 1 - day;
    const monday = new Date(date);
    monday.setDate(date.getDate() + diffToMonday);
    const key = monday.toISOString().split("T")[0];
    if (!weeks[key]) weeks[key] = [];
    weeks[key].push(entry);
  });
  return Object.entries(weeks)
    .map(([weekStart, weekEntries]) => ({ weekStart, entries: weekEntries }))
    .sort((a, b) => new Date(b.weekStart) - new Date(a.weekStart));
};

export default function DailyIntakeReport() {
  const { patientId, loading: resolvingPatientId, error: patientIdError } = useResolvedPatientId();
  const navigate = useNavigate();

  const [entries, setEntries] = useState([]);
  const [patientName, setPatientName] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!patientId) return;
    (async () => {
      setLoading(true);
      const result = await dailyIntakeService.getHistory(patientId);
      if (result.success) {
        // Backend response shape varies — sometimes a plain array, sometimes
        // an object with a nested .logs property. Same defensive handling
        // as DailyIntakeHistory.jsx, which already deals with this.
        const historyData = result.data;
        if (Array.isArray(historyData)) {
          setEntries(historyData);
        } else {
          setEntries(historyData?.logs || []);
        }
      } else {
        setError(result.message || "Could not load your health history.");
      }
      setLoading(false);
    })();
  }, [patientId]);

  if (resolvingPatientId || loading) {
    return (
      <Container sx={{ mt: 8, textAlign: "center" }}>
        <CircularProgress />
      </Container>
    );
  }

  if (patientIdError || error) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="error">{patientIdError || error}</Alert>
      </Container>
    );
  }

  const sorted = [...entries].sort((a, b) => new Date(b.date) - new Date(a.date));
  const sevenDaysAgo = new Date();
  sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

  const recentDaily = sorted.filter((e) => new Date(e.date) >= sevenDaysAgo);
  const olderEntries = sorted.filter((e) => new Date(e.date) < sevenDaysAgo);
  const weeklyGroups = groupByWeek(olderEntries);

  const anyAdverseEvent = (weekEntries) =>
    weekEntries.some((e) => e.fellToday || e.hospitalVisit || e.emergencyRoom || e.newSymptoms);

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 6 }}>
      <Box className="no-print" sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)}>
          Back
        </Button>
        <Button variant="contained" startIcon={<PrintIcon />} onClick={() => window.print()}>
          Print Report
        </Button>
      </Box>

      <Paper elevation={2} sx={{ p: 4 }}>
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          Health Summary Report
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Generated {new Date().toLocaleDateString("en-US", { weekday: "long", year: "numeric", month: "long", day: "numeric" })}
          {" · "}Covers {sorted.length} logged {sorted.length === 1 ? "day" : "days"}
        </Typography>

        {entries.length === 0 ? (
          <Alert severity="info">No daily check-in entries yet.</Alert>
        ) : (
          <>
            {/* Last 7 days — daily detail */}
            {recentDaily.length > 0 && (
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" fontWeight="bold" sx={{ mb: 1.5 }}>
                  Last 7 Days — Daily
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Date</strong></TableCell>
                        <TableCell><strong>Weight</strong></TableCell>
                        <TableCell><strong>BP</strong></TableCell>
                        <TableCell><strong>HR</strong></TableCell>
                        <TableCell><strong>O2</strong></TableCell>
                        <TableCell><strong>Symptoms Avg</strong></TableCell>
                        <TableCell><strong>Meds Taken</strong></TableCell>
                        <TableCell><strong>Flags</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {recentDaily.map((e) => (
                        <TableRow key={e.id}>
                          <TableCell>
                            {new Date(e.date).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                          </TableCell>
                          <TableCell>{e.weight ?? "—"}</TableCell>
                          <TableCell>
                            {e.bloodPressureSystolic && e.bloodPressureDiastolic
                              ? `${e.bloodPressureSystolic}/${e.bloodPressureDiastolic}` : "—"}
                          </TableCell>
                          <TableCell>{e.heartRate ?? "—"}</TableCell>
                          <TableCell>{e.oxygenSaturation ? `${e.oxygenSaturation}%` : "—"}</TableCell>
                          <TableCell>
                            {e.averageSymptomSeverity !== null && e.averageSymptomSeverity !== undefined
                              ? e.averageSymptomSeverity.toFixed(1) : "—"}
                          </TableCell>
                          <TableCell>{e.medicationsTaken ? "Yes" : "No"}</TableCell>
                          <TableCell>
                            {(e.fellToday || e.hospitalVisit || e.emergencyRoom || e.newSymptoms) ? (
                              <Chip label="See notes" size="small" color="warning" />
                            ) : "—"}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}

            {/* Older entries — weekly averages */}
            {weeklyGroups.length > 0 && (
              <Box>
                <Divider sx={{ my: 3 }} />
                <Typography variant="h6" fontWeight="bold" sx={{ mb: 1.5 }}>
                  Prior Weeks — Averages
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Week Of</strong></TableCell>
                        <TableCell><strong>Days Logged</strong></TableCell>
                        <TableCell><strong>Avg Weight</strong></TableCell>
                        <TableCell><strong>Avg BP</strong></TableCell>
                        <TableCell><strong>Avg HR</strong></TableCell>
                        <TableCell><strong>Avg O2</strong></TableCell>
                        <TableCell><strong>Avg Symptoms</strong></TableCell>
                        <TableCell><strong>Flags</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {weeklyGroups.map((group) => {
                        const avgWeight = average(group.entries.map((e) => e.weight));
                        const avgHR = average(group.entries.map((e) => e.heartRate));
                        const avgO2 = average(group.entries.map((e) => e.oxygenSaturation));
                        const avgSymptoms = average(group.entries.map((e) => e.averageSymptomSeverity));
                        return (
                          <TableRow key={group.weekStart}>
                            <TableCell>
                              {new Date(group.weekStart).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                            </TableCell>
                            <TableCell>{group.entries.length}</TableCell>
                            <TableCell>{avgWeight !== null ? avgWeight.toFixed(1) : "—"}</TableCell>
                            <TableCell>{formatBP(group.entries)}</TableCell>
                            <TableCell>{avgHR !== null ? Math.round(avgHR) : "—"}</TableCell>
                            <TableCell>{avgO2 !== null ? `${Math.round(avgO2)}%` : "—"}</TableCell>
                            <TableCell>{avgSymptoms !== null ? avgSymptoms.toFixed(1) : "—"}</TableCell>
                            <TableCell>
                              {anyAdverseEvent(group.entries) ? (
                                <Chip label="Event this week" size="small" color="warning" />
                              ) : "—"}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
          </>
        )}
      </Paper>

      <style>{`
        @page {
          margin: 0.5in;
        }
        @media print {
          .no-print { display: none !important; }
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        }
      `}</style>
    </Container>
  );
}